import {
  createFrameLayout,
  type AlignItems,
  type AlignSelf,
  type FrameLayout,
  type FrameLayoutKind,
  type FramePadding,
  type FrameNode,
  type JustifyContent,
  type Scene,
} from '../../scene/sceneState.js';
import {
  createCheckboxField,
  createNullableNumberInput,
  createNumberTupleInput,
  createNumberInput,
  createPropRow,
  createSectionHeader,
  createSubsectionGroup,
  type SceneTreeWidgetDeps,
} from './widgets.js';
import {
  findParentFrameId,
  frameLayoutDirection,
} from './queries.js';
import {
  setFramePropertyAndRender,
  type SceneTreePropertyActionDeps,
} from './propertyActions.js';
import type { AppState } from '../../state/appState.js';

export interface FramePropsDeps extends SceneTreePropertyActionDeps, SceneTreeWidgetDeps {
  appState: AppState;
  renderSceneTree: () => void;
}

const LAYOUT_KINDS: FrameLayoutKind[] = ['free', 'row', 'column', 'stack'];
const ALIGN_ITEMS: AlignItems[] = ['stretch', 'flex-start', 'center', 'flex-end'];
const ALIGN_SELF: AlignSelf[] = ['auto', 'stretch', 'flex-start', 'center', 'flex-end'];
const JUSTIFY_CONTENT: JustifyContent[] = ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'];
const PADDING_COMPONENTS = [
  { index: 0, title: 'Top' },
  { index: 1, title: 'Right' },
  { index: 2, title: 'Bottom' },
  { index: 3, title: 'Left' },
] as const;

function copyPadding(padding: FramePadding): FramePadding {
  return [padding[0], padding[1], padding[2], padding[3]];
}

function setFrameLayout(deps: FramePropsDeps, frame: FrameNode, layout: FrameLayout): void {
  setFramePropertyAndRender(deps, frame.id, 'layout', layout);
  deps.renderSceneTree();
}

function createLayoutFromKind(kind: FrameLayoutKind, previous: FrameLayout): FrameLayout {
  if (kind === 'free') return createFrameLayout('free');
  const defaults = createFrameLayout(kind);
  if (defaults.kind === 'free') return defaults;
  const previousBox = previous.kind === 'free' ? null : previous;
  const base = {
    padding: previousBox ? copyPadding(previousBox.padding) : copyPadding(defaults.padding),
    alignItems: previousBox?.alignItems ?? defaults.alignItems,
    justifyContent: previousBox?.justifyContent ?? defaults.justifyContent,
  };
  if (kind === 'stack') return { kind, ...base };
  const previousGap = previous.kind === 'row' || previous.kind === 'column' ? previous.gap : 0;
  return { kind, gap: previousGap, ...base };
}

function getLayoutAlignItems(layout: FrameLayout): AlignItems {
  return layout.kind === 'free' ? 'stretch' : layout.alignItems;
}

function getLayoutJustifyContent(layout: FrameLayout): JustifyContent {
  return layout.kind === 'free' ? 'flex-start' : layout.justifyContent;
}

function setRowDisabled(row: HTMLElement, disabled: boolean): void {
  row.classList.toggle('scene-prop-row--disabled', disabled);
}

function createLayoutNumberInput(
  deps: FramePropsDeps,
  value: number,
  enabled: boolean,
  onChange: (value: number) => void,
  historyLabel: string,
): HTMLElement {
  const input = createNumberInput(deps, value, 0, 9999, enabled ? onChange : () => {}, 1, enabled ? historyLabel : undefined, { subtype: 'pixels' });
  if (!enabled) input.classList.add('drag-field--disabled');
  return input;
}

function renderPaddingControls(deps: FramePropsDeps, panel: HTMLElement, frame: FrameNode, layout: FrameLayout): void {
  const enabled = layout.kind !== 'free';
  const row = createPropRow('padding');
  setRowDisabled(row, !enabled);
  row.appendChild(createNumberTupleInput(
    deps,
    layout.kind === 'free' ? [0, 0, 0, 0] as FramePadding : layout.padding,
    PADDING_COMPONENTS,
    value => {
      if (layout.kind === 'free') return;
      setFrameLayout(deps, frame, { ...layout, padding: value });
    },
    {
      min: 0,
      max: 9999,
      step: 1,
      enabled,
      historyLabel: 'Set frame padding',
      visual: { subtype: 'pixels' },
    },
  ));
  panel.appendChild(row);
}

function renderLayoutControls(deps: FramePropsDeps, panel: HTMLElement, frame: FrameNode): void {
  const layout = frame.layout;

  const gapEnabled = layout.kind === 'row' || layout.kind === 'column';
  const gapRow = createPropRow('gap');
  setRowDisabled(gapRow, !gapEnabled);
  gapRow.appendChild(createLayoutNumberInput(deps, gapEnabled ? layout.gap : 0, gapEnabled, value => {
    if (layout.kind === 'row' || layout.kind === 'column') {
      setFrameLayout(deps, frame, { ...layout, gap: value });
    }
  }, 'Set frame layout gap'));
  panel.appendChild(gapRow);

  renderPaddingControls(deps, panel, frame, layout);

  const clipRow = createPropRow('clip');
  const clipCheck = document.createElement('input');
  clipCheck.type = 'checkbox';
  clipCheck.checked = frame.clip;
  clipCheck.addEventListener('change', () => {
    const next = clipCheck.checked;
    if (frame.clip === next) return;
    setFramePropertyAndRender(deps, frame.id, 'clip', next);
  });
  clipRow.appendChild(createCheckboxField(clipCheck));
  panel.appendChild(clipRow);

  const alignmentGroup = createSubsectionGroup('alignment');
  const selectEnabled = layout.kind !== 'free';
  const alignRow = createPropRow('alignItems');
  setRowDisabled(alignRow, !selectEnabled);
  const alignSelect = document.createElement('select');
  alignSelect.className = 'scene-prop-input';
  alignSelect.disabled = !selectEnabled;
  for (const value of ALIGN_ITEMS) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = value;
    if (getLayoutAlignItems(layout) === value) opt.selected = true;
    alignSelect.appendChild(opt);
  }
  alignSelect.addEventListener('change', () => {
    if (layout.kind === 'free') return;
    const next = alignSelect.value as AlignItems;
    if (layout.alignItems === next) return;
    setFrameLayout(deps, frame, { ...layout, alignItems: next });
  });
  alignRow.appendChild(alignSelect);
  alignmentGroup.appendChild(alignRow);

  const justifyRow = createPropRow('justifyContent');
  setRowDisabled(justifyRow, !selectEnabled);
  const justifySelect = document.createElement('select');
  justifySelect.className = 'scene-prop-input';
  justifySelect.disabled = !selectEnabled;
  for (const value of JUSTIFY_CONTENT) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = value;
    if (getLayoutJustifyContent(layout) === value) opt.selected = true;
    justifySelect.appendChild(opt);
  }
  justifySelect.addEventListener('change', () => {
    if (layout.kind === 'free') return;
    const next = justifySelect.value as JustifyContent;
    if (layout.justifyContent === next) return;
    setFrameLayout(deps, frame, { ...layout, justifyContent: next });
  });
  justifyRow.appendChild(justifySelect);
  alignmentGroup.appendChild(justifyRow);
  panel.appendChild(alignmentGroup);
}

export function renderFrameProps(
  deps: FramePropsDeps,
  panel: HTMLElement,
  frame: FrameNode,
  activeScene: Scene,
): void {
  const appState = deps.appState;

  panel.appendChild(createSectionHeader('size'));

  const wRow = createPropRow('width');
  wRow.appendChild(createNullableNumberInput(deps, frame.width ?? null, value => {
    setFramePropertyAndRender(deps, frame.id, 'width', value);
  }, 100, 'Set frame width'));
  panel.appendChild(wRow);

  const hRow = createPropRow('height');
  hRow.appendChild(createNullableNumberInput(deps, frame.height ?? null, value => {
    setFramePropertyAndRender(deps, frame.id, 'height', value);
  }, 100, 'Set frame height'));
  panel.appendChild(hRow);

  panel.appendChild(createSectionHeader('layout'));

  const layoutRow = createPropRow('layout');
  const layoutSelect = document.createElement('select');
  layoutSelect.className = 'scene-prop-input';
  for (const kind of LAYOUT_KINDS) {
    const opt = document.createElement('option');
    opt.value = kind;
    opt.textContent = kind;
    if (frame.layout.kind === kind) opt.selected = true;
    layoutSelect.appendChild(opt);
  }
  layoutSelect.addEventListener('change', () => {
    const nextKind = layoutSelect.value as FrameLayoutKind;
    if (frame.layout.kind === nextKind) return;
    setFrameLayout(deps, frame, createLayoutFromKind(nextKind, frame.layout));
  });
  layoutRow.appendChild(layoutSelect);
  panel.appendChild(layoutRow);

  renderLayoutControls(deps, panel, frame);

  const frameParentLayout = (() => {
    const pid = findParentFrameId(activeScene, frame.id);
    const pf = pid ? activeScene.nodes[pid] : null;
    if (pf?.kind !== 'frame') return null;
    return frameLayoutDirection(appState, pf.layout);
  })();

  if (frameParentLayout === 'row' || frameParentLayout === 'column' || frameParentLayout === 'stack') {
    panel.appendChild(createSectionHeader('child'));
  }

  if (frameParentLayout === 'row' || frameParentLayout === 'column') {
    const fGrowRow = createPropRow('flexGrow');
    fGrowRow.appendChild(createNumberInput(deps, frame.flexGrow, 0, 100, v => { setFramePropertyAndRender(deps, frame.id, 'flexGrow', v); }, 0.1, 'Set frame flex-grow', { subtype: 'factor' }));
    panel.appendChild(fGrowRow);

    const fShrinkRow = createPropRow('flexShrink');
    fShrinkRow.appendChild(createNumberInput(deps, frame.flexShrink, 0, 100, v => { setFramePropertyAndRender(deps, frame.id, 'flexShrink', v); }, 0.1, 'Set frame flex-shrink', { subtype: 'factor' }));
    panel.appendChild(fShrinkRow);

    const fBasisRow = createPropRow('flexBasis');
    fBasisRow.appendChild(createNullableNumberInput(deps, frame.flexBasis, v => { setFramePropertyAndRender(deps, frame.id, 'flexBasis', v); }, 0, 'Set frame flex-basis'));
    panel.appendChild(fBasisRow);
  }

  if (frameParentLayout === 'row' || frameParentLayout === 'column' || frameParentLayout === 'stack') {
    const fAlignRow = createPropRow('alignSelf');
    const fAlignSelect = document.createElement('select');
    fAlignSelect.className = 'scene-prop-input';
    for (const val of ALIGN_SELF) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = val === 'auto' ? 'auto (inherit)' : val;
      if (frame.alignSelf === val) opt.selected = true;
      fAlignSelect.appendChild(opt);
    }
    fAlignSelect.addEventListener('change', () => {
      const next = fAlignSelect.value as AlignSelf;
      if (frame.alignSelf === next) return;
      setFramePropertyAndRender(deps, frame.id, 'alignSelf', next);
    });
    fAlignRow.appendChild(fAlignSelect);
    panel.appendChild(fAlignRow);
  }
}
